import {
  ArrowRight,
  ArrowsLeftRight,
  ChartBar,
  Megaphone,
  Money,
} from "@phosphor-icons/react";
import { Badge } from "flowbite-react";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { ColumnDef } from "@tanstack/react-table";

import { Table } from "../../../../components/Table";
import { useCampaignDetail } from "../../../../hooks/get/useCampaignDetail";
import { useCampaignBidHistory } from "../../../../hooks/get/useCampaignBidHistory";
import { useDateRange } from "../../../../contexts/DateRangeContext";
import { useIsMobile } from "../../../../hooks";
import { IDateRangePreset } from "../../../../interfaces";
import BaseCampaignHistory from "../../components/shared/BaseCampaignHistory";
import { BidHistory } from "../../../AppCountryReports/interface";

const GoogleAdsHistory: React.FC = () => {
  const { campaign_id } = useParams<{ campaign_id: string }>();
  const isMobile = useIsMobile();
  const { dateRange, setDateRange, selectedPreset, setSelectedPreset } = useDateRange();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  const { data: detailData } = useCampaignDetail({
    campaignId: campaign_id || "",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  const { data: historyData, isLoading } = useCampaignBidHistory({
    campaignId: parseInt(campaign_id || "0"),
  });


  const handleDateRangeChange = (startDate: Date, endDate: Date) => {
    setDateRange(startDate, endDate);
  };

  const handlePresetChange = (preset: IDateRangePreset | undefined) => {
    setSelectedPreset(preset);
  };

  const filteredHistory = useMemo(() => {
    const dataToFilter = historyData?.campaign_bid_histories || [];
    if (!searchTerm) return dataToFilter;
    const lowerSearch = searchTerm.toLowerCase();
    return dataToFilter.filter(
      (item) =>
        item.type?.toLowerCase().includes(lowerSearch) ||
        item.previous_data?.toString().toLowerCase().includes(lowerSearch) ||
        item.current_data?.toString().toLowerCase().includes(lowerSearch) ||
        item.user_name?.toLowerCase().includes(lowerSearch),
    );
  }, [historyData?.campaign_bid_histories, searchTerm]);

  const columns = useMemo<ColumnDef<BidHistory>[]>(
    () => [
      {
        accessorKey: "date",
        header: t("common.columns.dateTime", "Date & Time"),
        cell: ({ row }) => (
          <div className="text-sm text-gray-500 font-medium tabular-nums">
            {dayjs(row.original.date).format("MMM DD, YYYY HH:mm")}
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: t("common.columns.changeType", "Change Type"),
        cell: ({ row }) => {
          const type = row.original.type || "BID";
          let color = "info";
          let icon = <ChartBar size={14} weight="duotone" />;

          if (type === "BUDGET") {
            color = "success";
            icon = <Money size={14} weight="duotone" />;
          } else if (type === "BID" || type.includes("ROAS") || type.includes("CPC")) {
            color = "warning";
            icon = <ArrowsLeftRight size={14} weight="duotone" />;
          } else if (type === "STATUS") {
            color = "purple";
            icon = <Megaphone size={14} weight="duotone" />;
          }

          return (
            <div className="flex items-center gap-2">
              <Badge
                color={color}
                size="xs"
                className="px-2 py-0.5 rounded-md font-medium uppercase tracking-wider"
              >
                <div className="flex items-center gap-1">
                  {icon}
                  {type.replace("_", " ")}
                </div>
              </Badge>
            </div>
          );
        },
      },
      {
        id: "changes",
        header: t("common.columns.changes", "Changes"),
        cell: ({ row }) => {
          const symbol = row.original.currency_symbol || "";
          return (
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400 font-medium line-through decoration-gray-300 tabular-nums">
                {symbol}{row.original.previous_data}
              </span>
              <ArrowRight size={12} className="text-gray-400" weight="bold" />
              <span className="text-gray-800 font-bold tabular-nums">
                {symbol}{row.original.current_data}
              </span>
            </div>
          );
        },
      },
    ],
    [t],
  );

  return (
    <BaseCampaignHistory
      headerProps={{
        title: historyData?.campaign_name || detailData?.campaign?.google_campaign_name || detailData?.campaign?.campaign_name || t("campaigns.history.title", "Campaign History"),
        platform: "google_ads",
        type: "history",
        appInfo: {
          id: historyData?.google_campaign_id || detailData?.campaign?.google_campaign_id || detailData?.campaign?.campaign_id || campaign_id || "",
          name: (detailData?.campaign)?.app_name || historyData?.campaign_name || detailData?.campaign?.google_campaign_name || (detailData?.campaign)?.app_info?.app_name || detailData?.campaign?.campaign_name || "",
          logoUrl: (detailData?.campaign)?.app_logo || (detailData?.campaign)?.app_info?.app_logo || null,
        },
        onDateRangeChange: handleDateRangeChange,
        onPresetChange: handlePresetChange,
        dateRange: dateRange,
        selectedPresetId: selectedPreset?.id,
        onSearch: setSearchTerm,
        searchPlaceholder: t("common.search", "Search logs..."),
      }}
    >
      <Table
        data={filteredHistory}
        columns={columns}
        isLoading={isLoading}
        isMobile={isMobile}
        loadingRowCount={10}
      />
    </BaseCampaignHistory>
  );
};

export default GoogleAdsHistory;
